import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadSet2Bindings, buildKeyMap } from '../utils/keybindings';

const API_SYNC    = '/api/sync.php';
const POLL_MS     = 200;
const YT_POLL_MS  = 200;

function fmtTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m   = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.5, 2];

function VideoPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'dual'; // 'dual' | 'ipad'

  // ── Stato video ─────────────────────────────────────────────────────────
  const [sourceType, setSourceType] = useState('none'); // 'none' | 'youtube' | 'url'
  const [urlInput,   setUrlInput]   = useState('');
  const [localSrc,   setLocalSrc]   = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]  = useState(0);
  const [speed,       setSpeed]     = useState(1);
  const [ytReady,     setYtReady]   = useState(false);

  // ── Stato scoreboard (ricevuto via BC o sync.php) ───────────────────────
  const [sbSession,      setSbSession]      = useState(null);
  const [sbScores,       setSbScores]       = useState({ blue: 0, red: 0 });
  const [sbTimer,        setSbTimer]        = useState({ display: '00:00.0', isRunning: false });
  const [sbRound,        setSbRound]        = useState(1);
  const [sbRoundMarkers, setSbRoundMarkers] = useState([]);

  const videoRef       = useRef(null);
  const ytPlayerRef    = useRef(null);
  const ytDivRef       = useRef(null);
  const channelRef     = useRef(null);
  const lastCmdRef     = useRef(0);
  const videoLoadedRef = useRef(false); // Evita reload multipli in ipad mode

  // ── Controlli player ────────────────────────────────────────────────────
  const execPlay = useCallback(() => {
    ytPlayerRef.current?.playVideo?.();
    videoRef.current?.play?.();
  }, []);

  const execPause = useCallback(() => {
    ytPlayerRef.current?.pauseVideo?.();
    videoRef.current?.pause?.();
  }, []);

  const execSeek = useCallback((t) => {
    if (ytPlayerRef.current?.seekTo) ytPlayerRef.current.seekTo(t, true);
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
    // Broadcast immediato: aggiorna currentTimeRef in Window 1 anche da video in pausa
    channelRef.current?.postMessage({ type: 'timeupdate', time: t });
  }, []);

  const execSpeed = useCallback((r) => {
    setSpeed(r);
    ytPlayerRef.current?.setPlaybackRate?.(r);
    if (videoRef.current) videoRef.current.playbackRate = r;
  }, []);

  const getTime = useCallback(() => {
    if (ytPlayerRef.current?.getCurrentTime) return ytPlayerRef.current.getCurrentTime();
    if (videoRef.current) return videoRef.current.currentTime;
    return 0;
  }, []);

  // ── Carica YouTube ──────────────────────────────────────────────────────
  const loadYouTube = useCallback(async (videoId) => {
    if (!window.YT?.Player) {
      await new Promise(resolve => {
        window.onYouTubeIframeAPIReady = resolve;
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const s = document.createElement('script');
          s.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(s);
        }
      });
    }
    ytPlayerRef.current?.destroy?.();
    ytPlayerRef.current = new window.YT.Player(ytDivRef.current, {
      videoId,
      playerVars: { autoplay: 0, controls: 0 },
      events: {
        onReady: (e) => {
          setDuration(e.target.getDuration());
          setYtReady(true);
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            channelRef.current?.postMessage({ type: 'play' });
          }
          if (e.data === window.YT.PlayerState.PAUSED) {
            channelRef.current?.postMessage({ type: 'pause' });
          }
        },
      },
    });
  }, []);

  // ── Carica URL ──────────────────────────────────────────────────────────
  const handleLoad = useCallback((urlOverride) => {
    const url = urlOverride || urlInput;
    if (!url.trim()) return;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      setSourceType('youtube');
      setYtReady(false);
      loadYouTube(ytMatch[1]);
    } else {
      setLocalSrc(url);
      setSourceType('url');
    }
  }, [urlInput, loadYouTube]);

  // ── Auto-load video URL (modalità dual) ────────────────────────────────
  useEffect(() => {
    if (mode !== 'dual') return;
    const savedUrl = localStorage.getItem('tkd_dual_video_url');
    if (savedUrl) {
      setUrlInput(savedUrl);
      handleLoad(savedUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── BroadcastChannel — modalità dual ────────────────────────────────────
  useEffect(() => {
    if (mode !== 'dual') return;
    const ch = new BroadcastChannel('tkd-sync');
    channelRef.current = ch;

    ch.onmessage = (e) => {
      const { type, time, rate, session, scores, timer, currentRound, roundMarkers } = e.data;
      if (type === 'play')    execPlay();
      if (type === 'pause')   execPause();
      if (type === 'seek')    execSeek(time);
      if (type === 'speed')   execSpeed(rate);
      if (type === 'keydown') window.dispatchEvent(new KeyboardEvent('keydown', { key: e.data.key, bubbles: true }));
      if (type === 'state') {
        if (session)      setSbSession(session);
        if (scores)       setSbScores(scores);
        if (timer)        setSbTimer(timer);
        if (currentRound) setSbRound(currentRound);
        if (roundMarkers) setSbRoundMarkers(roundMarkers);
      }
    };

    return () => { ch.close(); channelRef.current = null; };
  }, [mode, execPlay, execPause, execSeek, execSpeed]);

  // ── Polling YouTube currentTime → broadcast timeupdate (modalità dual) ──
  useEffect(() => {
    if (mode !== 'dual' || !ytReady) return;
    const iv = setInterval(() => {
      const t = ytPlayerRef.current?.getCurrentTime?.() || 0;
      setCurrentTime(t);
      channelRef.current?.postMessage({ type: 'timeupdate', time: t });
    }, YT_POLL_MS);
    return () => clearInterval(iv);
  }, [mode, ytReady]);

  // ── Polling sync.php — modalità ipad ────────────────────────────────────
  useEffect(() => {
    if (mode !== 'ipad') return;

    const poll = async () => {
      try {
        const res     = await fetch(API_SYNC);
        const { data } = await res.json();
        if (!data) return;

        // Aggiorna scoreboard
        if (data.session) setSbSession(data.session);
        setSbScores(data.scores   || { blue: 0, red: 0 });
        setSbTimer({ display: data.timerDisplay || '00:00.0', isRunning: data.timerRunning || false });
        setSbRound(data.currentRound || 1);

        // Auto-carica video dalla sessione (solo una volta, usa ref per evitare restart loop)
        if (!videoLoadedRef.current && data.session?.video_source) {
          videoLoadedRef.current = true;
          setUrlInput(data.session.video_source);
          handleLoad(data.session.video_source);
        }

        // Esegui comandi dell'iPad
        if (data.commandId && data.commandId !== lastCmdRef.current) {
          lastCmdRef.current = data.commandId;
          const cmd = data.command;
          if (cmd === 'play')  execPlay();
          if (cmd === 'pause') execPause();
          if (cmd === 'seek'  && data.commandTime  != null) execSeek(data.commandTime);
          if (cmd === 'speed' && data.commandSpeed != null) execSpeed(data.commandSpeed);

          // Cancella il comando eseguito
          fetch(API_SYNC, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ command: null }),
          }).catch(() => {});
        }

        // Pubblica posizione corrente del video
        const ct = getTime();
        const isPlaying = videoRef.current ? !videoRef.current.paused : false;
        fetch(API_SYNC, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentTime: parseFloat(ct.toFixed(2)), playing: isPlaying }),
        }).catch(() => {});

      } catch { /* ignora errori di rete */ }
    };

    const iv = setInterval(poll, POLL_MS);
    poll(); // prima richiesta immediata
    return () => clearInterval(iv);
  }, [mode, execPlay, execPause, execSeek, execSpeed, getTime, handleLoad]);

  // ── Handlers click pulsanti ─────────────────────────────────────────────
  const doPlay = useCallback(() => {
    execPlay();
    if (mode === 'dual') channelRef.current?.postMessage({ type: 'play' });
  }, [execPlay, mode]);
  const doPause = useCallback(() => {
    execPause();
    if (mode === 'dual') channelRef.current?.postMessage({ type: 'pause' });
  }, [execPause, mode]);
  const doSeek = useCallback((t) => {
    execSeek(t);
    if (mode === 'dual') channelRef.current?.postMessage({ type: 'seek', time: t });
  }, [execSeek, mode]);
  const doSpeed = (r) => {
    execSpeed(r);
    if (mode === 'dual') channelRef.current?.postMessage({ type: 'speed', rate: r });
    if (mode === 'ipad') {
      fetch(API_SYNC, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ speed: r }),
      }).catch(() => {});
    }
  };

  // ── Keyboard shortcuts globali ─────────────────────────────────────────
  const doPlayRef      = useRef(doPlay);
  const doPauseRef     = useRef(doPause);
  const doSeekRef      = useRef(doSeek);
  const getTimeRef     = useRef(getTime);
  const bindingsRef    = useRef(loadSet2Bindings());
  useEffect(() => { doPlayRef.current = doPlay; doPauseRef.current = doPause; doSeekRef.current = doSeek; getTimeRef.current = getTime; });

  // Tasti di analisi (B/R/A/D/1-6/Enter/Escape/ecc.) forwarded a window 1 via BC
  const ACTION_KEYS = new Set(['b','r','a','d','x','g','c','1','2','3','4','6','+','enter','escape']);

  useEffect(() => {
    const handler = (e) => {
      try {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        // In dual mode, forward tasti azione a window 1
        if (mode === 'dual' && ACTION_KEYS.has(e.key.toLowerCase())) {
          channelRef.current?.postMessage({ type: 'keydown', key: e.key });
          return;
        }
        const keyMap   = buildKeyMap(bindingsRef.current);
        const actionId = keyMap[e.code];
        if (actionId === 'video_play_pause') {
          e.preventDefault();
          let playing = false;
          try { playing = videoRef.current ? !videoRef.current.paused : ytPlayerRef.current?.getPlayerState?.() === 1; } catch {}
          playing ? doPauseRef.current() : doPlayRef.current();
        } else if (actionId === 'video_seek_fwd') {
          e.preventDefault();
          doSeekRef.current(getTimeRef.current() + (e.shiftKey ? 5 : 1));
        } else if (actionId === 'video_seek_back') {
          e.preventDefault();
          doSeekRef.current(Math.max(0, getTimeRef.current() - (e.shiftKey ? 5 : 1)));
        } else if (actionId === 'video_frame_back') {
          e.preventDefault();
          doSeekRef.current(Math.max(0, getTimeRef.current() - 0.04));
        } else if (actionId === 'video_frame_fwd') {
          e.preventDefault();
          doSeekRef.current(getTimeRef.current() + 0.04);
        }
      } catch {}
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // registrato una sola volta

  // ── Zoom timeline sul round corrente ─────────────────────────────────────
  const roundStart = sbRoundMarkers.find(m => m.round === sbRound && m.type === 'start');
  const roundEnd   = sbRoundMarkers.find(m => m.round === sbRound && m.type === 'end');
  const isZoomed   = !!(roundStart && roundEnd && duration > 0);
  // viewStart/viewEnd usati SOLO se entrambi i marker esistono, altrimenti video intero
  const viewStart  = isZoomed ? roundStart.timestamp_video : 0;
  const viewEnd    = isZoomed ? roundEnd.timestamp_video   : duration;
  const viewRange  = (viewEnd - viewStart) || duration || 1;
  const timeToPct  = (t) => Math.min(100, Math.max(0, ((t - viewStart) / viewRange) * 100));
  const progressPct = duration > 0 ? timeToPct(currentTime) : 0;

  const blueName = sbSession?.athlete_blue_name || 'BLU';
  const redName  = sbSession?.athlete_red_name  || 'ROSSO';

  return (
    <div style={{
      background: 'var(--bg-primary)', height: '100vh', display: 'flex',
      flexDirection: 'column', color: '#fff', overflow: 'hidden',
    }}>
      {/* ── Scoreboard ─────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-primary)', borderBottom: '1px solid var(--color-border)',
        padding: '6px 16px', display: 'flex', alignItems: 'center',
        gap: 12, flexShrink: 0, userSelect: 'none',
      }}>
        <span style={{ color: 'var(--color-blue)', fontWeight: 800, fontSize: 16, textTransform: 'uppercase' }}>
          {blueName}
        </span>
        <span style={{ color: 'var(--color-blue)', fontFamily: 'monospace', fontSize: 30, fontWeight: 900, minWidth: 44, textAlign: 'center' }}>
          {sbScores.blue}
        </span>
        <span style={{
          fontFamily: 'monospace', fontSize: 22, fontWeight: 700,
          color: sbTimer.isRunning ? 'var(--color-green)' : 'var(--color-text-sub)',
          background: 'var(--bg-secondary)', border: `1px solid ${sbTimer.isRunning ? 'var(--color-green)' : 'var(--bg-elevated)'}`,
          borderRadius: 6, padding: '2px 12px', minWidth: 100, textAlign: 'center',
        }}>
          {sbTimer.display}
        </span>
        <span style={{ color: 'var(--color-red)', fontFamily: 'monospace', fontSize: 30, fontWeight: 900, minWidth: 44, textAlign: 'center' }}>
          {sbScores.red}
        </span>
        <span style={{ color: 'var(--color-red)', fontWeight: 800, fontSize: 16, textTransform: 'uppercase' }}>
          {redName}
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--color-text-dim)', fontSize: 12 }}>
          R{sbRound} · {mode === 'ipad' ? '📱 iPad' : '🖥🖥 Dual'}
        </span>
      </div>

      {/* ── Area video ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative', background: 'var(--bg-primary)', overflow: 'hidden', minHeight: 0 }}>

        {/* Nessun video caricato */}
        {sourceType === 'none' && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
          }}>
            <div style={{ color: 'var(--bg-elevated)', fontSize: 48 }}>🎬</div>
            <div style={{ color: 'var(--color-text-dim)', fontSize: 15 }}>Nessun video caricato</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--color-border-hover)', color: 'var(--color-text)',
                  padding: '8px 12px', borderRadius: 6, fontSize: 14, width: 380,
                  outline: 'none',
                }}
                placeholder="URL YouTube o video MP4..."
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLoad()}
              />
              <button
                style={{
                  background: 'var(--color-blue)', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14,
                }}
                onClick={() => handleLoad()}
              >Carica</button>
              <button
                style={{
                  background: 'var(--bg-elevated)', color: 'var(--color-text-sub)', border: 'none',
                  borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                }}
                onClick={() => {
                  const i = document.createElement('input');
                  i.type = 'file'; i.accept = 'video/*';
                  i.onchange = e => {
                    const f = e.target.files[0];
                    if (f) { setLocalSrc(URL.createObjectURL(f)); setSourceType('url'); }
                  };
                  i.click();
                }}
              >File</button>
            </div>
          </div>
        )}

        {/* YouTube */}
        {sourceType === 'youtube' && (
          <div ref={ytDivRef} style={{ width: '100%', height: '100%' }} />
        )}

        {/* HTML5 video */}
        {sourceType === 'url' && localSrc && (
          <video
            ref={videoRef}
            src={localSrc}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onPlay={() => {
              if (mode === 'dual') channelRef.current?.postMessage({ type: 'play' });
            }}
            onPause={() => {
              if (mode === 'dual') channelRef.current?.postMessage({ type: 'pause' });
            }}
            onSeeked={() => {
              if (!videoRef.current) return;
              const t = videoRef.current.currentTime;
              setCurrentTime(t);
              if (mode === 'dual') channelRef.current?.postMessage({ type: 'timeupdate', time: t });
            }}
            onTimeUpdate={() => {
              if (!videoRef.current) return;
              const t = videoRef.current.currentTime;
              setCurrentTime(t);
              if (mode === 'dual') channelRef.current?.postMessage({ type: 'timeupdate', time: t });
            }}
            onLoadedMetadata={() => {
              if (videoRef.current) setDuration(videoRef.current.duration);
            }}
          />
        )}
      </div>

      {/* ── Controlli ──────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-primary)', borderTop: '1px solid var(--color-border)',
        padding: '8px 12px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6,
      }}>

        {/* Label zoom round */}
        {isZoomed && (
          <div style={{ fontSize: 10, color: 'var(--color-orange)', textAlign: 'center', letterSpacing: 0.5 }}>
            ⬛ R{sbRound} · {fmtTime(viewStart)} → {fmtTime(viewEnd)}
          </div>
        )}

        {/* Progress bar */}
        <div
          style={{ height: 14, background: 'var(--bg-card)', borderRadius: 4, cursor: 'pointer', flexShrink: 0, position: 'relative', border: '1px solid var(--color-border)' }}
          onClick={e => {
            if (!duration) return;
            const r = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientX - r.left) / r.width;
            doSeek(viewStart + ratio * viewRange);
          }}
        >
          <div style={{ height: '100%', background: 'var(--color-blue)', borderRadius: 3, width: `${progressPct}%`, transition: 'width 0.1s linear' }} />
          {/* Round markers */}
          {duration > 0 && sbRoundMarkers
            .filter(m => m.timestamp_video != null && m.timestamp_video >= viewStart && m.timestamp_video <= viewEnd)
            .map(m => {
              const color = m.type === 'start' ? 'var(--color-green)' : 'var(--color-orange)';
              return (
                <div key={`rm-${m.id}`}
                  onClick={e => { e.stopPropagation(); doSeek(m.timestamp_video); }}
                  title={`R${m.round} ${m.type === 'start' ? 'Inizio' : 'Fine'}: ${m.timestamp_display}`}
                  style={{
                    position: 'absolute', left: `${timeToPct(m.timestamp_video)}%`,
                    top: 0, bottom: 0, width: 3, background: color,
                    transform: 'translateX(-50%)', cursor: 'pointer', borderRadius: 2,
                    boxShadow: `0 0 6px ${color}`,
                  }}
                />
              );
            })}
        </div>

        {/* Pulsanti controllo */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            style={{ background: 'var(--color-green)', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
            onClick={doPlay}
          >▶</button>
          <button
            style={{ background: 'var(--bg-elevated)', color: 'var(--color-text-sub)', border: 'none', borderRadius: 5, padding: '5px 14px', cursor: 'pointer', fontSize: 14 }}
            onClick={doPause}
          >⏸</button>
          {/* Avanza/indietro frame */}
          <button
            style={{ background: 'var(--bg-elevated)', color: 'var(--color-text-dim)', border: '1px solid var(--color-border)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}
            onClick={() => doSeek(Math.max(0, getTime() - 0.04))}
            title="Frame indietro"
          >◀◀</button>
          <button
            style={{ background: 'var(--bg-elevated)', color: 'var(--color-text-dim)', border: '1px solid var(--color-border)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}
            onClick={() => doSeek(getTime() + 0.04)}
            title="Frame avanti"
          >▶▶</button>

          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-text-dim)', minWidth: 100 }}>
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>

          {/* Velocità */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 3 }}>
            {SPEEDS.map(s => (
              <button
                key={s}
                style={{
                  background: speed === s ? 'var(--color-orange)' : 'var(--bg-card)',
                  color: speed === s ? '#fff' : 'var(--color-text-dim)',
                  border: `1px solid ${speed === s ? 'var(--color-orange)' : 'var(--color-border)'}`,
                  borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11,
                  fontWeight: speed === s ? 700 : 400,
                }}
                onClick={() => doSpeed(s)}
              >{s}x</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPage;
